import type { BadgeRole, NoteMark } from '@feral/shared/client'
import {
  NOTE_RATINGS,
  NOTE_VIBES,
  NOTE_WARNINGS,
  type NoteWarning,
  worstWarning,
} from '@feral/shared/options/notes'
import { Handle } from '@feral/ui'
import type { ReactNode } from 'react'
import { useMark } from './noteKeys.js'

/**
 * What you noted about someone, worn where you meet them (decision 51): the worst warning on their
 * avatar, your name for them beside their handle. Only ever drawn for the note's writer.
 */
export function WarningBadge({ warnings, size }: { warnings: readonly NoteWarning[]; size: number }) {
  const worst = worstWarning(warnings)
  if (!worst) return null
  const all = warnings.map((w) => NOTE_WARNINGS[w].label).join(', ')
  return (
    <span
      className="fm-mark-badge"
      role="img"
      aria-label={`Your warning: ${all}`}
      data-tip={`Your warning: ${all}`}
      style={{ fontSize: Math.max(11, Math.round(size * 0.32)) }}
    >
      {NOTE_WARNINGS[worst].emoji}
      {warnings.length > 1 && <span className="fm-mark-badge__more">{warnings.length}</span>}
    </span>
  )
}

/** An avatar with your warning badge on its lower left (the position badge has the lower right). */
export function WithWarning({ id, size, children }: { id: string; size: number; children: ReactNode }) {
  const mark = useMark(id)
  if (!mark?.warnings.length) return <>{children}</>
  return (
    <span className="fm-mark-wrap">
      {children}
      <WarningBadge warnings={mark.warnings} size={size} />
    </span>
  )
}

/**
 * Their standing in front (Handle, IC-R7), then your name for them and their handle, quieter. `role` is
 * the highest a row has, or every one on a profile.
 */
export function NameWithAlias({
  id,
  handle,
  role,
  size,
}: {
  id: string
  handle: string | null
  role?: BadgeRole | readonly BadgeRole[] | null | undefined
  size?: number
}) {
  const alias = useMark(id)?.alias
  return (
    <Handle role={role} size={size}>
      {!alias || alias === handle ? (
        (handle ?? 'A member')
      ) : (
        <>
          {alias} <span className="fm-mark-handle">{handle ?? 'A member'}</span>
        </>
      )}
    </Handle>
  )
}

export const ratingOf = (n: number | null | undefined) => NOTE_RATINGS.find((r) => r.value === n) ?? null

/** Rating and vibes as a row of emoji, labeled for screen readers. */
export function MarkLine({ mark }: { mark: Pick<NoteMark, 'rating' | 'vibes'> }) {
  const r = ratingOf(mark.rating)
  if (!r && !mark.vibes.length) return null
  const label = [r?.label, ...mark.vibes.map((v) => NOTE_VIBES[v].label)].filter(Boolean).join(', ')
  return (
    <span className="fm-mark-line" role="img" aria-label={label} title={label}>
      {r?.emoji}
      {mark.vibes.map((v) => NOTE_VIBES[v].emoji).join('')}
    </span>
  )
}

/** Every warning you gave them, over their photos on the full profile, to you only. */
export function ProfileWarnings({ id }: { id: string }) {
  const warnings = useMark(id)?.warnings ?? []
  if (!warnings.length) return null
  return (
    <ul className="fm-pager-warnings" aria-label="Your warnings">
      {warnings.map((w) => (
        <li key={w} title={NOTE_WARNINGS[w].label}>
          <span aria-hidden="true">{NOTE_WARNINGS[w].emoji}</span> {NOTE_WARNINGS[w].label}
        </li>
      ))}
    </ul>
  )
}
